import { Check, CircleAlert, CircleDashed, Clock, X, type LucideIcon } from "lucide-react";

import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { CardholderBoardItem } from "../types/cardholderOnboarding.types";
import { cardholderTrack, type TrackStepState } from "../utils/cardholderBoard";

const STATE_STYLE: Record<TrackStepState, { icon: LucideIcon; className: string }> = {
  done: { icon: Check, className: "bg-success/10 text-success dark:bg-success/20" },
  current: {
    icon: Clock,
    className: "bg-blue-500/10 text-blue-700 dark:bg-blue-400/20 dark:text-blue-300",
  },
  blocked: { icon: CircleAlert, className: "bg-warning/10 text-warning dark:bg-warning/20" },
  failed: { icon: X, className: "bg-destructive/10 text-destructive dark:bg-destructive/20" },
  pending: { icon: CircleDashed, className: "border border-dashed border-border text-muted-foreground" },
};

export interface CardholderProgressProps {
  item: CardholderBoardItem;
  providerLabel: string;
  headline: string;
  className?: string;
}

/**
 * Registration → verification → card provider: one chip per step, and the sentence that says where it is
 * held.
 */
export function CardholderProgress({ item, providerLabel, headline, className }: CardholderProgressProps) {
  const { t } = useTranslations("cardholderOnboarding");
  const track = cardholderTrack(item);
  const steps = [
    { id: "registration", label: t("step_registration"), state: track.registration },
    { id: "verification", label: t("step_verification"), state: track.verification },
    { id: "card", label: providerLabel, state: track.card },
  ];

  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <ol className="flex flex-wrap items-center gap-1" aria-label={t("track_label")}>
        {steps.map((step, index) => {
          const { icon: Icon, className: stateClass } = STATE_STYLE[step.state];
          return (
            <li key={step.id} className="flex items-center gap-1">
              {index > 0 && <span aria-hidden className="h-px w-3 bg-border" />}
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap",
                  stateClass,
                )}
              >
                <Icon className="size-3" aria-hidden />
                {step.label}
                <span className="sr-only">: {t(`state_${step.state}`)}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground">{headline}</p>
    </div>
  );
}
